import { Plug, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { StackLink } from '@/app/navigation/StackLink';
import { SidebarMenuButton } from '@/components/ui/Sidebar/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useIsOnline,
  useServiceWorkerEnabled,
  useSyncStatus,
} from './useSyncStatus';
import { describeSyncStatus } from './describeSyncStatus';

const PILL_CLASS = `bg-muted my-auto flex h-8 w-auto shrink-0 flex-row gap-2 rounded-full border p-2
  group-data-[collapsible=icon]:h-auto! group-data-[collapsible=icon]:flex-col`;

/**
 * Global sync/online indicator — the icon pill next to the user avatar in
 * the sidebar footer (stacks vertically when the sidebar collapses). State
 * is conveyed by color only: the sync icon spins while syncing, turns amber
 * with queued offline changes and red on errors; the plug shows
 * connectivity. Clicking opens the sync status panel on the second stack.
 *
 * The SW is opt-in: until installed the pill renders as a muted placeholder
 * hinting that offline sync isn't set up — still clickable, the panel is
 * where installation happens.
 */
export function SyncStatusIndicator() {
  const swEnabled = useServiceWorkerEnabled();
  const status = useSyncStatus();
  const online = useIsOnline();
  const intl = useIntl();

  if (!swEnabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarMenuButton
            asChild
            size="sm"
            className={`${PILL_CLASS} text-muted-foreground opacity-50`}
            data-testid="sync-status"
          >
            <StackLink to="/sync-status" stack={1}>
              <RefreshCw size={24} />
              <Plug size={24} />
            </StackLink>
          </SidebarMenuButton>
        </TooltipTrigger>
        <TooltipContent side="right">
          <div className="space-y-1">
            <p>Offline sync is not set up on this device</p>
            <p className="text-muted-foreground">Click to install</p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  const view = describeSyncStatus(status, online, (date) =>
    intl.formatRelativeTime(
      Math.round((date.getTime() - Date.now()) / 60_000),
      'minutes',
    ),
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <SidebarMenuButton
          asChild
          size="sm"
          className={PILL_CLASS}
          data-testid="sync-status"
        >
          <StackLink to="/sync-status" stack={1}>
            <RefreshCw
              size={24}
              className={`${view.spinning ? 'animate-spin' : ''} ${view.syncColorClass}`}
            />
            <Plug
              size={24}
              className={
                online
                  ? 'text-green-600 dark:text-green-500'
                  : 'text-destructive'
              }
            />
          </StackLink>
        </SidebarMenuButton>
      </TooltipTrigger>
      <TooltipContent side="right">
        <div className="space-y-1">
          <p>
            {online ? 'Online' : 'Offline'} — {view.label}
          </p>
          <p>{view.detail}</p>
          {view.pendingDetail && <p>{view.pendingDetail}</p>}
          <p className="text-muted-foreground">Click for sync details</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
